import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const BALLOON_COLORS = ["pink", "purple", "blue", "amber", "emerald"];
const MAX_POPS_PER_BOOTH = 5;

interface BalloonAssignment {
  oderId: string;
  balloonNumber: number;
  balloonColor: string;
  popCount: number;
}

export const useBoothBalloons = (
  boothId: string,
  currentUserId?: string,
  currentUserName?: string
) => {
  const [balloonAssignments, setBalloonAssignments] = useState<Map<string, BalloonAssignment>>(new Map());
  const [popCounts, setPopCounts] = useState<Map<string, number>>(new Map());
  const [poppedUsers, setPoppedUsers] = useState<Set<string>>(new Set());
  const [hasShownEntryToast, setHasShownEntryToast] = useState(false);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // Count how many pops current user has done in this booth
  const popsUsed = useMemo(() => poppedUsers.size, [poppedUsers]);
  const popsRemaining = MAX_POPS_PER_BOOTH - popsUsed;

  // Assign balloon to a user
  const assignBalloon = useCallback((userId: string): BalloonAssignment => {
    const existingAssignment = balloonAssignments.get(userId);
    if (existingAssignment) return existingAssignment;

    const balloonNumber = balloonAssignments.size + 1;
    const balloonColor = BALLOON_COLORS[balloonNumber % BALLOON_COLORS.length];

    const assignment: BalloonAssignment = {
      oderId: userId,
      balloonNumber,
      balloonColor,
      popCount: 0,
    };

    setBalloonAssignments(prev => new Map(prev).set(userId, assignment));
    return assignment;
  }, [balloonAssignments]);

  // Get balloon for a user (creates if doesn't exist)
  const getBalloon = useCallback((userId: string): BalloonAssignment => {
    return balloonAssignments.get(userId) || assignBalloon(userId);
  }, [balloonAssignments, assignBalloon]);

  // Current user's balloon
  const currentUserBalloon = useMemo(() => {
    if (!currentUserId) return null;
    return getBalloon(currentUserId);
  }, [currentUserId, getBalloon]);

  // Show entry toast once
  useEffect(() => {
    if (currentUserBalloon && !hasShownEntryToast && currentUserId) {
      toast.success(`🎈 You're Balloon #${currentUserBalloon.balloonNumber}`, {
        description: `You have ${MAX_POPS_PER_BOOTH} pops to use in this booth!`,
        duration: 4000,
      });
      setHasShownEntryToast(true);
    }
  }, [currentUserBalloon, hasShownEntryToast, currentUserId]);

  // Subscribe to pop events via realtime
  useEffect(() => {
    if (!boothId || !currentUserId) return;

    const channel = supabase.channel(`booth-balloons:${boothId}`);
    channelRef.current = channel;

    channel
      .on("broadcast", { event: "balloon_pop" }, (payload) => {
        const { toUserId, fromUserId, fromUserName } = payload.payload as {
          fromUserId: string;
          toUserId: string;
          fromUserName: string;
        };

        // Update pop count for the target user
        setPopCounts(prev => {
          const newCounts = new Map(prev);
          newCounts.set(toUserId, (newCounts.get(toUserId) || 0) + 1);
          return newCounts;
        });

        // Show toast if someone popped current user's balloon
        if (toUserId === currentUserId) {
          toast(`💖 ${fromUserName} popped your balloon!`, {
            duration: 3000,
          });
        }
      })
      .on("broadcast", { event: "mutual_match" }, (payload) => {
        const { user1Name, user2Name, user1Id, user2Id } = payload.payload as {
          user1Id: string;
          user2Id: string;
          user1Name: string;
          user2Name: string;
        };

        // Show match notification if current user is involved
        if (user1Id === currentUserId || user2Id === currentUserId) {
          const otherName = user1Id === currentUserId ? user2Name : user1Name;
          toast.success(`🎉 It's a match with ${otherName}!`, {
            description: "You both popped each other's balloon!",
            duration: 5000,
          });
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [boothId, currentUserId]);

  // Pop someone's balloon (with limit check)
  const popBalloon = useCallback(async (targetUserId: string) => {
    if (!currentUserId || !currentUserName || targetUserId === currentUserId) return false;

    // Check if already popped this user
    if (poppedUsers.has(targetUserId)) {
      toast.error("You already popped this person's balloon!");
      return false;
    }

    // Check pop limit
    if (popsRemaining <= 0) {
      toast.error("You've used all your pops in this booth!");
      return false;
    }

    // Track this pop locally
    setPoppedUsers(prev => new Set(prev).add(targetUserId));

    // Send pop event
    if (channelRef.current) {
      await channelRef.current.send({
        type: "broadcast",
        event: "balloon_pop",
        payload: {
          fromUserId: currentUserId,
          toUserId: targetUserId,
          fromUserName: currentUserName,
          timestamp: Date.now(),
        },
      });
    }

    // Record in database for match tracking
    try {
      await supabase.from("arena_interactions").insert({
        arena_id: boothId,
        from_user_id: currentUserId,
        to_user_id: targetUserId,
        interaction_type: "balloon_pop",
      });

      // Check for mutual pop (match)
      const { data: mutualPop } = await supabase
        .from("arena_interactions")
        .select("id")
        .eq("arena_id", boothId)
        .eq("from_user_id", targetUserId)
        .eq("to_user_id", currentUserId)
        .eq("interaction_type", "balloon_pop")
        .maybeSingle();

      if (mutualPop && channelRef.current) {
        // Broadcast mutual match
        const targetBalloon = getBalloon(targetUserId);
        await channelRef.current.send({
          type: "broadcast",
          event: "mutual_match",
          payload: {
            user1Id: currentUserId,
            user2Id: targetUserId,
            user1Name: currentUserName,
            user2Name: `Balloon #${targetBalloon.balloonNumber}`,
          },
        });
        return true; // indicates a match
      }
    } catch (error) {
      console.error("Failed to record balloon pop:", error);
    }

    // Update local count immediately
    setPopCounts(prev => {
      const newCounts = new Map(prev);
      newCounts.set(targetUserId, (newCounts.get(targetUserId) || 0) + 1);
      return newCounts;
    });

    toast.success(`You popped their balloon 💖`);
    return false;
  }, [boothId, currentUserId, currentUserName, poppedUsers, popsRemaining, getBalloon]);

  // Get pop count for a user
  const getPopCount = useCallback((userId: string): number => {
    return popCounts.get(userId) || 0;
  }, [popCounts]);

  // Check if current user can pop a specific user
  const canPopUser = useCallback((userId: string): boolean => {
    if (!currentUserId || userId === currentUserId) return false;
    if (poppedUsers.has(userId)) return false;
    if (popsRemaining <= 0) return false;
    return true;
  }, [currentUserId, poppedUsers, popsRemaining]);

  return {
    getBalloon,
    currentUserBalloon,
    popBalloon,
    getPopCount,
    canPopUser,
    popsRemaining,
    popsUsed,
    maxPops: MAX_POPS_PER_BOOTH,
    balloonAssignments,
  };
};
